<template>
  <div>
    <v-combobox
      :value="value"
      :items="suggestions"
      :loading="loading"
      :search-input.sync="typed"
      :label="label"
      :hint="hint"
      persistent-hint
      hide-no-data
      no-filter
      :error-messages="errorMessages"
      @input="setDescription"
    />
    <v-alert v-if="duplicateOf" type="warning" text dense class="mt-3 mb-0 caption">
      <strong>{{ duplicateOf.fooddescriptionsimple || duplicateOf.fooddescription }}</strong>
      [{{ duplicateOf.foodid }}] is already described this way. Two ingredients sharing a
      description is how the misleading second lines happen &mdash; describe this one on its own terms.
    </v-alert>
    <v-card v-if="formatted" outlined class="mt-3">
      <v-card-subtitle class="pb-0 caption">Will be saved as</v-card-subtitle>
      <v-card-text class="pb-2"><strong>{{ formatted }}</strong></v-card-text>
      <v-card-subtitle class="pt-0 pb-0 caption">Shows on the ingredients list as</v-card-subtitle>
      <v-card-text>
        <strong>{{ name || 'the ingredient name' }}</strong><br/>
        {{ formatted }}
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import api from '@/api';

// ingredient descriptions follow the nutrient-file convention: the food first,
// then how it is prepared, comma separated and getting more specific as you go
// (eg "Onion, raw" / "Pork, loin, boneless, broiled").  Keep that shape rather
// than letting people write a sentence.
export const EXAMPLE_DESCRIPTIONS = [
  'Onion, raw',
  'Broccoli, boiled, drained',
  'Pork, loin, centre cut, boneless, broiled'
];

export function formatDescription(description) {
  const segments = (description || '')
    .split(',')
    .map(segment => segment.replace(/\s+/g, ' ').trim())
    .filter(segment => segment !== '');
  if (segments.length === 0) {
    return '';
  }
  segments[0] = segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  return segments.join(', ');
}

export default {
  name: 'IngredientDescriptionField',
  props: {
    value: {type: String, default: ''},
    // the ingredient name, so the preview matches what the list will show
    name: {type: String, default: ''},
    label: {type: String, default: 'Description'},
    // ingredient the description must not be silently inherited from
    excludeFoodid: {type: Number, default: null}
  },
  data() {
    return {
      typed: this.value,
      matches: [],
      loading: false,
      lastSearch: null
    }
  },
  watch: {
    value(v) {
      this.typed = v;
    },
    typed(query) {
      this.search(query);
    }
  },
  computed: {
    formatted() {
      return formatDescription(this.value);
    },
    hint() {
      return `The food, then how it is prepared, separated by commas - eg ${EXAMPLE_DESCRIPTIONS.join('; ')}`;
    },
    // existing descriptions to start from, so a new one lands in the same shape
    suggestions() {
      return [...new Set(this.matches
        .map(match => (match.fooddescription || '').trim())
        .filter(description => description !== ''))];
    },
    duplicateOf() {
      const formatted = this.formatted.toLowerCase();
      if (!formatted) {
        return null;
      }
      return this.matches.find(match =>
        match.foodid !== this.excludeFoodid &&
        (match.fooddescription || '').trim().toLowerCase() === formatted) || null;
    },
    errorMessages() {
      const typed = (this.typed || '').trim();
      if (typed && !this.formatted) {
        return ['Enter a description, not just punctuation'];
      }
      return [];
    }
  },
  methods: {
    setDescription(description) {
      this.$emit('input', description || '');
    },
    search(query) {
      const trimmed = (query || '').trim();
      if (trimmed.length < 2 || this.loading) {
        return;
      }
      // the search endpoint matches on description as well as name, so an exact
      // match tells us the description is already taken
      const url = `/foodname/${encodeURIComponent(trimmed)}?limit=100&minimal=true`;
      if (this.lastSearch === url) {
        return;
      }
      this.lastSearch = url;
      this.loading = true;
      api.get(url)
        .then(res => {
          this.matches = res.data || [];
        })
        .catch(e => console.log('description search failed', e))
        .finally(() => (this.loading = false));
    }
  }
}
</script>
